import { useState, useCallback } from 'react';
import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://server.ciertacorp.com',
  timeout: 1000000, // Default timeout for regular requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If data is FormData, let axios set Content-Type automatically with boundary
    // Don't override Content-Type for FormData - axios will set it correctly
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = String(error.config?.url || '');
      const isLoginAttempt = reqUrl.includes('/auth/login');
      if (!isLoginAttempt) {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const useHttp = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const request = useCallback(async (config) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api(config);
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const get = useCallback((url, config = {}) => {
    return request({ method: 'GET', url, ...config });
  }, [request]);

  const post = useCallback((url, data, config = {}) => {
    // If data is FormData, ensure Content-Type is not set (axios will set it automatically)
    const headers = data instanceof FormData 
      ? { ...config.headers } // Don't set Content-Type for FormData
      : { ...config.headers };
    
    // Remove Content-Type from headers if FormData (axios will add it with boundary)
    if (data instanceof FormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    }
    
    // Increase timeout for FormData requests (file uploads take longer - handles dozens of files)
    const timeout = data instanceof FormData 
      ? (config.timeout || 300000) // 5 minutes for file uploads (handles dozens of files)
      : (config.timeout || 10000); // 10 seconds for regular requests
    
    return request({ method: 'POST', url, data, ...config, headers, timeout });
  }, [request]);

  const put = useCallback((url, data, config = {}) => {
    // If data is FormData, ensure Content-Type is not set (axios will set it automatically)
    const headers = data instanceof FormData 
      ? { ...config.headers } // Don't set Content-Type for FormData
      : { ...config.headers };
    
    // Remove Content-Type from headers if FormData (axios will add it with boundary)
    if (data instanceof FormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    }
    
    // Increase timeout for FormData requests (file uploads take longer - handles dozens of files)
    const timeout = data instanceof FormData 
      ? (config.timeout || 300000) // 5 minutes for file uploads (handles dozens of files)
      : (config.timeout || 10000); // 10 seconds for regular requests
    
    return request({ method: 'PUT', url, data, ...config, headers, timeout });
  }, [request]);

  const patch = useCallback((url, data, config = {}) => {
    return request({ method: 'PATCH', url, data, ...config });
  }, [request]);

  const del = useCallback((url, config = {}) => {
    return request({ method: 'DELETE', url, ...config });
  }, [request]);

  const upload = useCallback((url, formData, config = {}) => {
    // Don't set Content-Type for FormData - axios will set it automatically with boundary
    const headers = { ...config.headers };
    // Remove Content-Type if it exists - axios will add it with proper boundary
    delete headers['Content-Type'];
    
    // Increase timeout for file uploads (default 5 minutes for dozens of files)
    const timeout = config.timeout || 300000;
    
    return request({
      method: 'POST',
      url,
      data: formData,
      headers,
      timeout,
      ...config,
    });
  }, [request]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    get,
    post,
    put,
    patch,
    delete: del,
    upload,
    clearError,
  };
};

export default api;

